import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Button,
  Card,
  Chip,
} from '../../components';
import { useApiKey, useStatusPolling } from '../../hooks';
import { formatRelativeTime, getGradeColor } from '../../utils';
import type { AdminInspectionListItem, AdminInspectionFilters, InspectionStatus, UUID } from '../../types/api';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey, storeId, storeName, clear, isConfigured } = useApiKey();
  const [inspections, setInspections] = useState<AdminInspectionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AdminInspectionFilters>({
    status: undefined,
    grade: undefined,
    store_id: storeId || undefined,
    page: 1,
    pageSize: 20,
    search: '',
  });
  const [total, setTotal] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState<InspectionStatus | 'all'>('all');
  const [activeTab, setActiveTab] = useState(0);

  // Poll for status updates on visible inspections
  const processingIds = inspections
    .filter(i => i.status === 'processing')
    .map(i => i.inspection_id);

  const { refresh: refreshStatus } = useStatusPolling({
    inspectionIds: processingIds,
    apiKey: apiKey ?? undefined,
    enabled: processingIds.length > 0,
    interval: 10000,
    onStatusChange: (updated) => {
      setInspections(prev => prev.map(insp => {
        const update = updated.find(u => u.inspection_id === insp.inspection_id);
        if (update) {
          return { ...insp, status: update.status, updated_at: update.updated_at, reason: update.reason };
        }
        return insp;
      }));
    },
  });

  const fetchInspections = async () => {
    if (!apiKey) return;
    
    setLoading(true);
    try {
      // In a real app, this would be a proper list endpoint
      // For now, we'll simulate with mock data or use the status endpoint
      // Since there's no list endpoint in the contract, we'll use mock data
      const mockData: AdminInspectionListItem[] = [
        {
          inspection_id: '550e8400-e29b-41d4-a716-446655440001',
          plate: 'ABC1D23',
          store_id: 'loja-001',
          store_name: 'Loja Teste 1',
          status: 'done',
          grade: 'B',
          score: 78,
          created_at: '2024-01-15T10:30:00Z',
          updated_at: '2024-01-15T10:35:12Z',
          client_name: 'João Silva',
          client_phone: '(11) 99999-9999',
        },
        {
          inspection_id: '550e8400-e29b-41d4-a716-446655440002',
          plate: 'XYZ9K88',
          store_id: 'loja-001',
          store_name: 'Loja Teste 1',
          status: 'processing',
          created_at: '2024-01-15T11:00:00Z',
          updated_at: '2024-01-15T11:00:00Z',
        },
        {
          inspection_id: '550e8400-e29b-41d4-a716-446655440003',
          plate: 'FOO2B11',
          store_id: 'loja-001',
          store_name: 'Loja Teste 1',
          status: 'failed',
          reason: 'image_quality:rear_too_dark',
          created_at: '2024-01-15T09:00:00Z',
          updated_at: '2024-01-15T09:05:00Z',
        },
        {
          inspection_id: '550e8400-e29b-41d4-a716-446655440004',
          plate: 'BAR3C22',
          store_id: 'loja-001',
          store_name: 'Loja Teste 1',
          status: 'done',
          grade: 'A',
          score: 92,
          created_at: '2024-01-14T15:30:00Z',
          updated_at: '2024-01-14T15:35:00Z',
        },
        {
          inspection_id: '550e8400-e29b-41d4-a716-446655440005',
          plate: 'QWE4R55',
          store_id: 'loja-001',
          store_name: 'Loja Teste 1',
          status: 'done',
          grade: 'C',
          score: 45,
          created_at: '2024-01-14T10:00:00Z',
          updated_at: '2024-01-14T10:05:00Z',
        },
      ];

      // Apply filters
      let filtered = mockData;
      if (filters.status) {
        filtered = filtered.filter(i => i.status === filters.status);
      }
      if (filters.grade) {
        filtered = filtered.filter(i => i.grade === filters.grade);
      }
      if (filters.search) {
        const search = filters.search.toLowerCase();
        filtered = filtered.filter(i => 
          i.plate.toLowerCase().includes(search) ||
          i.client_name?.toLowerCase().includes(search)
        );
      }

      setInspections(filtered);
      setTotal(filtered.length);
    } catch (error) {
      console.error('Failed to fetch inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConfigured) {
      fetchInspections();
    }
  }, [isConfigured, filters]);

  const handleInspectionClick = (inspection: AdminInspectionListItem) => {
    navigate(`/admin/vistoria/${inspection.inspection_id}`, { replace: true });
  };

  const handleReprocess = async (_inspectionId: UUID) => {
    if (!confirm('Reprocessar esta vistoria?')) return;
    
    try {
      // Would call reprocess API
      // For now, just refresh status
      await refreshStatus();
    } catch (error) {
      console.error('Reprocess failed:', error);
    }
  };

  const handleLogout = () => {
    clear();
    navigate('/admin/login', { replace: true });
  };

  const stats = {
    total: inspections.length,
    processing: inspections.filter(i => i.status === 'processing').length,
    done: inspections.filter(i => i.status === 'done').length,
    failed: inspections.filter(i => i.status === 'failed').length,
    avgScore: inspections.filter(i => i.score !== undefined).reduce((sum, i) => sum + (i.score || 0), 0) / 
              Math.max(inspections.filter(i => i.score !== undefined).length, 1),
  };

  return (
    <Page
      appBar={{
        title: 'Dashboard',
        subtitle: storeName ?? undefined,
        actions: [
          <Button variant="icon" onClick={handleLogout} aria-label="Sair">
            <span className="material-symbols-outlined">logout</span>
          </Button>
        ],
      }}
      bottomNav={{
        items: [
          { id: 'dashboard', label: 'Dashboard', icon: <span className="material-symbols-outlined">dashboard</span>, activeIcon: <span className="material-symbols-outlined">dashboard</span> },
          { id: 'list', label: 'Vistorias', icon: <span className="material-symbols-outlined">list_alt</span>, activeIcon: <span className="material-symbols-outlined">list_alt</span> },
          { id: 'reports', label: 'Relatórios', icon: <span className="material-symbols-outlined">assessment</span>, activeIcon: <span className="material-symbols-outlined">assessment</span> },
        ],
        activeId: activeTab === 0 ? 'dashboard' : activeTab === 1 ? 'list' : 'reports',
        onChange: (id) => setActiveTab(id === 'dashboard' ? 0 : id === 'list' ? 1 : 2),
      }}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card variant="elevated" padding="md" className="text-center">
          <p className="font-display-lg text-primary tabular-nums">{stats.total}</p>
          <p className="font-label-sm text-on-surface-variant">Total</p>
        </Card>
        <Card variant="elevated" padding="md" className="text-center">
          <p className="font-display-lg text-warning tabular-nums">{stats.processing}</p>
          <p className="font-label-sm text-on-surface-variant">Processando</p>
        </Card>
        <Card variant="elevated" padding="md" className="text-center">
          <p className="font-display-lg text-success tabular-nums">{stats.done}</p>
          <p className="font-label-sm text-on-surface-variant">Concluídas</p>
        </Card>
        <Card variant="elevated" padding="md" className="text-center">
          <p className="font-display-lg text-error tabular-nums">{stats.failed}</p>
          <p className="font-label-sm text-on-surface-variant">Falhas</p>
        </Card>
      </div>

      {/* Filters */}
      <Card variant="elevated" padding="md" className="mb-4">
        <div className="flex flex-wrap gap-2">
          <Chip
            variant={selectedStatus === 'all' ? 'primary' : 'default'}
            onClick={() => { setSelectedStatus('all'); setFilters(f => ({ ...f, status: undefined, page: 1 })); }}
          >
            Todas
          </Chip>
          <Chip
            variant={selectedStatus === 'processing' ? 'processing' : 'default'}
            onClick={() => { setSelectedStatus('processing'); setFilters(f => ({ ...f, status: 'processing', page: 1 })); }}
          >
            Processando
          </Chip>
          <Chip
            variant={selectedStatus === 'done' ? 'success' : 'default'}
            onClick={() => { setSelectedStatus('done'); setFilters(f => ({ ...f, status: 'done', page: 1 })); }}
          >
            Concluídas
          </Chip>
          <Chip
            variant={selectedStatus === 'failed' ? 'error' : 'default'}
            onClick={() => { setSelectedStatus('failed'); setFilters(f => ({ ...f, status: 'failed', page: 1 })); }}
          >
            Falhas
          </Chip>
        </div>
      </Card>

      {/* Inspections List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <span className="material-symbols-outlined animate-spin-slow text-primary text-3xl">sync</span>
        </div>
      ) : inspections.length === 0 ? (
        <Card variant="outlined" padding="xl" className="text-center">
          <span className="material-symbols-outlined text-on-surface-variant text-4xl mb-3 block">inbox</span>
          <h3 className="font-headline-sm">Nenhuma vistoria encontrada</h3>
          <p className="font-body-sm text-on-surface-variant mt-1">
            {selectedStatus !== 'all' ? 'Tente alterar os filtros' : 'As vistorias da loja aparecerão aqui'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {inspections.map((inspection) => (
            <Card
              key={inspection.inspection_id}
              variant="interactive"
              padding="md"
              onClick={() => handleInspectionClick(inspection)}
            >
              <div className="flex items-center gap-4">
                {/* Status Badge */}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 
                  ${inspection.status === 'processing' ? 'bg-primary-fixed' : 
                     inspection.status === 'done' ? 'bg-success-bg' : 'bg-error-container'}">
                  <span className="material-symbols-outlined 
                    ${inspection.status === 'processing' ? 'text-primary animate-spin-slow' : 
                       inspection.status === 'done' ? 'text-success' : 'text-error'}">
                    {inspection.status === 'processing' ? 'sync' : 
                     inspection.status === 'done' ? 'check_circle' : 'error'}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-headline-sm truncate">{inspection.plate}</h3>
                    {inspection.grade && (
                      <Chip
                        variant={getGradeColor(inspection.grade) as any}
                        size="sm"
                      >
                        Nota {inspection.grade}
                      </Chip>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 flex-wrap">
                    <span className="font-body-sm text-on-surface-variant">
                      {formatRelativeTime(inspection.created_at)}
                    </span>
                    {inspection.score !== undefined && (
                      <span className="font-body-sm text-on-surface-variant">
                        Score: <span className="font-label-md tabular-nums">{inspection.score}</span>
                      </span>
                    )}
                    {inspection.client_name && (
                      <span className="font-body-sm text-on-surface-variant">
                        {inspection.client_name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {inspection.status === 'failed' && (
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleReprocess(inspection.inspection_id); }}>
                      <span className="material-symbols-outlined text-sm">refresh</span>
                      Reprocessar
                    </Button>
                  )}
                  <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination placeholder */}
      {total > filters.pageSize && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={filters.page === 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>
            Anterior
          </Button>
          <span className="font-label-md">Página {filters.page}</span>
          <Button variant="outline" size="sm" disabled={filters.page * filters.pageSize >= total} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>
            Próxima
          </Button>
        </div>
      )}
    </Page>
  );
};